import React, {useMemo} from "react";
import {Box, TextField, MenuItem, Checkbox, ListSubheader, Typography, Grid, Select, FormControl, InputLabel} from "@mui/material";
import {inputTools} from "../InputTools/constant.js";
import {isMobile, parseJSON} from "../App/utils.js";
import {KBName} from "../names";
import {getGroupedModels} from "./Chat";
import {ON_PREMISES_CHAT_API_URL, ON_PREMISES_CHAT_MODELS} from "../API/chatAPI";
import {colorEasyGreen, colorGreen} from "../App/theme";


export const renderChatModelsSelect = ({
                                           withoutObsolete,
                                           groupedModels,
                                           onChange,
                                           model,
                                           details = true,
                                           noneOption,
                                           style = {},
                                           variant = "outlined",
                                           withHeaders = true,
                                       }) => {
    const formatPrice = (price, n = 5) => {
        return `$${(price / (100 * 1000)).toFixed(n)}`;
    };
    const formatContextSize = (size) => `${size / 1000}K`;
    const precision = (size) => {
        if (size === "Large") return 3;
        if (size === "Medium") return 4;
        return 5;
    };

    const renderValue = (selected) => {
        const selectedModel = Object.values(groupedModels)
            .flat()
            .find((m) => m.id === selected);
        let label = selectedModel ? selectedModel.name : noneOption || "";

        return (
            <Box position="relative" display="inline-block" width="100%">
                <Typography variant="body1">{label}</Typography>
                {selectedModel?.isOnPremises && (
                    <Typography
                        variant="caption"
                        color={colorGreen}
                        position="absolute"
                        bottom={-6}
                        right={-20}
                        style={{ fontSize: '0.45rem' }}
                    >
                        On-Premises
                    </Typography>
                )}
            </Box>
        );
    };

    return (
        <Box mt={2} sx={{ width: "100%", ...style }}>
            <TextField
                select
                value={model}
                onChange={(e) => {
                    const modelId = e.target.value;
                    let vendor = null;

                    // Iterate over all sizes dynamically
                    Object.keys(groupedModels).forEach(size => {
                        const models = groupedModels[size];
                        const model = models.find(m => m.id === modelId);
                        if (model) {
                            vendor = model.vendor;
                        }
                    });

                    onChange(e.target.value, vendor);
                }}
                fullWidth
                variant={variant}
                label={details ? "Chat Model" : undefined}
                InputProps={
                    variant === "outlined"
                        ? {}
                        : {
                            disableUnderline: true,
                            style: { padding: 0 },
                        }
                }
                SelectProps={{
                    renderValue
                }}
            >
                {withHeaders && (
                    <ListSubheader style={{ paddingBottom: "8px" }} key={"first"}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={5}>
                                <Typography variant="body2">Model</Typography>
                            </Grid>

                            <>
                                <Grid item xs={5}>
                                    <Typography variant="body2">input / output</Typography>
                                </Grid>
                                {details && (
                                    <Grid item xs={1}>
                                        <Typography variant="body2">Max Context</Typography>
                                    </Grid>
                                )}
                            </>
                        </Grid>
                    </ListSubheader>
                )}

                {noneOption && (
                    <MenuItem value={""} key={"none"}>
                        <Typography variant="body1">{noneOption}</Typography>
                    </MenuItem>
                )}

                {Object.entries(groupedModels).map(
                    ([size, models]) =>
                        (size !== "Obsolete" || !withoutObsolete) && [
                            <ListSubheader key={size}>{size}</ListSubheader>,
                            models
                                .filter((model) => !model?.obsolete || !withoutObsolete)
                                .map((myModel) => (
                                    <MenuItem value={myModel.id} key={myModel.id}>
                                        <Grid container spacing={2} alignItems="center">
                                            <Grid
                                                item
                                                xs={5}
                                                style={{ marginLeft: 6, marginRight: 6 }}
                                            >
                                                <Typography variant="body2">{myModel.name}</Typography>
                                            </Grid>
                                            <>
                                                <Grid item xs={5}>
                                                    <Typography variant="body2">
                                                        {myModel.inputPrice
                                                            ? `${formatPrice(
                                                                myModel.inputPrice,
                                                                precision(myModel.size)
                                                            )} / ${formatPrice(
                                                                myModel.outputPrice,
                                                                precision(myModel.size)
                                                            )}`
                                                            : "On-premises model"}
                                                    </Typography>
                                                </Grid>
                                                {details && (
                                                    <Grid item xs={1}>
                                                        <Typography variant="body2">
                                                            {formatContextSize(myModel.context)}
                                                        </Typography>
                                                    </Grid>
                                                )}
                                            </>
                                        </Grid>
                                    </MenuItem>
                                )),
                        ]
                )}
            </TextField>
        </Box>
    );
};

const ChatSettings = ({KBData, setKBData, network}) => {
    const groupedModels = useMemo(getGroupedModels, []);

    const formatPrice = (price, n = 5) => `$${(price / (100*1000)).toFixed(n)}`;


    const hasImageEditor = KBData?.inputTools?.map(o => parseJSON(o))?.some(o => o?.type === 'imageEditor')

    const handleChangeInputTools = (event) => {

        let inputToolsPerType = event.target.value.reduce((map, item) => {
            const type = parseJSON(item)?.type || inputTools?.[item]?.type || item;

            const existingItems = map.get(type) || [];
            if (type === "imageEditor") {
                // allow multiple tools per type (able to select more than one)
                return map.set(type, [...existingItems, item]);
            } else {
                // ensure unique tools per type (unable to select more than one of this type)
                return map.set(type, [item]);
            }
        }, new Map());

        let multipleInputToolsPerType = Array.from(inputToolsPerType.values()).flat();

        // console.log('multipleInputToolsPerType', multipleInputToolsPerType)

        setKBData({
            ...KBData,
            inputTools: multipleInputToolsPerType
        });
    };

    return (
        <Box mt={1}>
            <Box mt={2} sx={{ marginTop: '16px', width: '100%', marginBottom: '16px' }}>
                <TextField
                    inputProps={{
                        autoComplete: 'kb-title',
                        form: {
                            autoComplete: 'off',
                        },
                    }}
                    label={KBName + " Title"}
                    value={KBData.kbTitle}
                    onChange={(e) => setKBData({
                        ...KBData,
                        kbTitle: e.target.value
                    })}
                    fullWidth
                    required
                    variant="outlined"
                />
            </Box>

            <Box mt={2} sx={{ marginTop: '16px', width: '100%', marginBottom: '16px' }}>
                <TextField
                    label="Base Instructions"
                    value={KBData.kbInstructions}
                    onChange={(e) => setKBData({
                        ...KBData,
                        kbInstructions: e.target.value
                    })}
                    fullWidth
                    multiline
                    minRows={4}
                    variant="outlined"
                    inputProps={{ style: { maxHeight: '300px', overflow: 'auto' } }}
                />
            </Box>

            {KBData?.kbTitle && renderChatModelsSelect({
                groupedModels,
                onChange: (model) => setKBData({ ...KBData, model}),
                model: KBData?.model
            })}

            <Box mt={2} sx={{ marginTop: '16px', width: '100%', marginBottom: '16px' }}>
                {/* New uploader options */}
                <TextField
                    select
                    label="Tools"
                    value={KBData.inputTools || [
                        // 'imageToText'
                    ]}
                    onChange={handleChangeInputTools}
                    fullWidth
                    variant="outlined"
                    SelectProps={{
                        multiple: true,
                        renderValue: (selected) => (
                            <Box display="flex">
                                {selected.map((item, i) => {
                                    const itemJSONType = parseJSON(item)?.type;
                                    const key = itemJSONType ? itemJSONType : item;
                                    const props = {key:i + key, style: { marginRight: 10 }};
                                    return <div key={i}>{(inputTools?.[key]?.iconSettings?.(props) || inputTools?.[key]?.icon(props))}</div>;
                                })}
                            </Box>
                        )
                    }}
                >
                    {Object.keys(inputTools).filter(key => !inputTools[key].hidden).map(key => {
                        const tool = inputTools[key];
                        const serviceName = tool?.service?.name;
                        const pricePerRequest = tool?.service?.pricePerRequest;
                        const pricePerMinute = tool?.service?.pricePerMinute;
                        const isImageEditor = key === 'imageEditor';

                        // Use the existing icon logic, adjusting for the imageEditor case
                        const icon = isImageEditor ? (tool?.iconSettings?.() || tool.icon()) : tool.icon();

                        let title = serviceName
                            ? (isMobile ? tool?.desc : serviceName + ' - ' + tool?.desc)
                            : tool.settingsLabel || tool.label



                        // Determine the label to use, adjusting for the imageEditor case
                        const label = isImageEditor
                            ? "Image editor" + (isMobile ? '' : ' - ' + tool?.desc)
                            : title;

                        // Determine the price display text, adjusting for the imageEditor case
                        let priceText = isImageEditor ? "" :
                            (pricePerRequest ? `${formatPrice(pricePerRequest, 4)}${!isMobile ? ' per request' : '/req'}` : '') +
                            (pricePerMinute ? `${formatPrice(pricePerMinute, 4)}${!isMobile ? ' per minute' : '/min'}` : '');

                        return (
                            <MenuItem value={isImageEditor ? JSON.stringify({ type: 'imageEditor', vendor: 'openkbs'}) : key} key={key}>
                                <Grid container alignItems="center" justifyContent="space-between">
                                    {/* Left side: Checkbox and ListItemText on the same line */}
                                    <Grid item xs={isMobile ? 8 : 4} container alignItems="center">
                                        <Checkbox
                                            icon={icon}
                                            checkedIcon={icon}
                                            checked={KBData?.inputTools?.indexOf(key) > -1 || !!KBData?.inputTools?.find(o => parseJSON(o)?.type === 'imageEditor')}
                                        />
                                        <Typography variant="body2">
                                            {label}
                                        </Typography>
                                    </Grid>

                                    {/* Center: Price */}
                                    <Grid item xs={isMobile ? 4 : 4} style={{ textAlign: 'center' }}>
                                        <Box display="flex" justifyContent="flex-start" style={{ paddingLeft: isMobile ? '5%' : '25%' }}>
                                            <Typography variant="body2">
                                                {priceText}
                                            </Typography>
                                        </Box>
                                    </Grid>

                                    {/* Right side: Placeholder for consistency, can be used for additional info */}
                                    <Grid item xs={isMobile ? 0 : 4} style={{ textAlign: 'right' }}>
                                        {/* Placeholder or additional component like <ServiceInterfaces endpoint={service} /> */}
                                    </Grid>
                                </Grid>
                            </MenuItem>
                        );
                    })}
                </TextField>
            </Box>
        </Box>
    );
};

export default ChatSettings;
